import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Table, Button, Badge, Form, Spinner, Offcanvas, Tabs, Tab } from 'react-bootstrap';
import { saveAs } from 'file-saver';
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer';
import { displayDate, displayOutDate } from '../../utils/date';
import { getMovementTypeMeta } from '../../utils/movementType';
import { getVisiblePages } from '../../utils/pagination';
import {
  getInventoryReports,
  getInventoryReportFolios,
  getInventoryReportFoliosDetailed,
  getSoldFolioRanges,
  getSoldFoliosDetailed,
  getInventoryReportTotals,
  getInventoryReportFull,
} from '../../services/Reports/Reports';
import { getCategories } from '../../services/Configs/ItemCategoriesService';
import { getStatuses } from '../../services/Other/GeneralQueries';
import { getItemFolioRanges } from '../../services/Items/Inventory';
import { MyReport } from './RequestReport';

const Reports = () => {
  // catálogos
  const [categories, setCategories] = useState([]);
  const [statuses, setStatuses] = useState([]);

  // filtros
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState(0);
  const [folioInput, setFolioInput] = useState('');
  const [folioText, setFolioText] = useState(''); // este es el que viaja al backend
  const [movementType, setMovementType] = useState(''); // '' = todos

  // data
  const [inventory, setInventory] = useState([]);
  const [folioRanges, setFolioRanges] = useState([]);
  const [folioDetailed, setFolioDetailed] = useState([]);
  const [folioMap, setFolioMap] = useState({});
  const [totals, setTotals] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // ui
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  // paginación (server-side)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTab, setPreviewTab] = useState('pdf');

  // Clave consumibles
  const [supplyInput, setSupplyInput] = useState('');
  const [supplyText, setSupplyText] = useState('');

  const [pdfData, setPdfData] = useState([]);
  const [pdfFoliosDetailed, setPdfFoliosDetailed] = useState([]);
  const [pdfFolioMap, setPdfFolioMap] = useState({});
  const [loadingPdfData, setLoadingPdfData] = useState(false);

  const pageCount = useMemo(() => {
    const pc = Math.ceil((Number(totalCount) || 0) / pageSize);
    return pc;
  }, [totalCount, pageSize]);

  const totalsList = totals;

  const formatTotal = (t) => {
    const v = t?.total_products ?? t?.total_quantity ?? 0;
    return Number(v).toLocaleString('es-MX');
  };

  const categoryBadge = (name) => {
    const n = (name || '').trim().toLowerCase();

    const map = {
      'materia prima': { bg: 'primary' },
      'procesado': { bg: 'warning', text: 'dark' },
      'terminado': { bg: 'success' },
      'producto terminado': { bg: 'success' },
      'producción en proceso': { bg: 'warning', text: 'dark' },
    };

    const s = map[n] || { bg: 'secondary' };
    return <Badge bg={s.bg} text={s.text}>{name || 'N/D'}</Badge>;
  };

  const conceptBadge = (type, fallback = '') => {
    const meta = getMovementTypeMeta(type || fallback);
    if (!meta) return '—';

    return (
      <Badge bg={meta.bg} text={meta.text}>
        {meta.label}
      </Badge>
    );
  };

  const formatFolio = (r) => {
    const count = Number(r.count ?? 0);
    const isSingle = count <= 1 || r.startFolioName === r.endFolioName;
    return isSingle
      ? r.startFolioName
      : `${r.startFolioName} – ${r.endFolioName} (${count})`;
  };

  const formatFoliosForCell = (ranges = []) =>
    ranges.map(formatFolio).join(' | ');

  const flattenFolioNames = (ranges = []) =>
    ranges.flatMap((r) => Array.isArray(r.folios) ? r.folios.map((f) => f.name) : []);

  const statusBadge = (name) => {
    const n = (name || '').toLowerCase();

    const bg =
      n.includes('vend') ? 'success' :
        n.includes('cortes') ? 'info' :
          n.includes('merma') ? 'danger' :
            n.includes('promo') ? 'warning' :
              n.includes('degusta') ? 'secondary' :
                n.includes('agot') ? 'dark' :
                  n.includes('dispon') ? 'primary' :
                    n.includes('proce') ? 'warning' :
                      'dark';

    const text = n.includes('promo') ? 'dark' : undefined;

    return <Badge bg={bg} text={text}>{name || 'N/D'}</Badge>;
  };

  const getRowFolios = (item) => {
    const ranges = folioMap[item.id] || [];

    console.log('[REPORTS][getRowFolios]', {
      itemId: item.id,
      itemName: item.name,
      ranges,
    });

    return ranges;
  };

  const foliosByItemId = useMemo(() => {
    const map = {};
    (folioDetailed || []).forEach((r) => {
      const k = Number(r.itemId);
      if (!map[k]) map[k] = [];
      map[k].push(r.folioName);
    });
    return map;
  }, [folioDetailed]);

  const folioRangesByItemId = useMemo(() => {
    const map = {};
    (folioRanges || []).forEach((r) => {
      const key = Number(r.itemId);
      if (!map[key]) map[key] = [];

      const label =
        Number(r.count) <= 1 || r.startFolioName === r.endFolioName
          ? r.startFolioName
          : `${r.startFolioName} – ${r.endFolioName}`;

      map[key].push(label);
    });
    return map;
  }, [folioRanges]);

  useEffect(() => {
    console.log('[REPORTS] foliosByItemId map:', foliosByItemId);
  }, [foliosByItemId]);

  useEffect(() => {
    console.log('[REPORTS] folioRangesByItemId map:', folioRangesByItemId);
  }, [folioRangesByItemId]);

  useEffect(() => {
    const t = (supplyInput || '').trim();
    const h = setTimeout(() => setSupplyText(t), 350);
    return () => clearTimeout(h);
  }, [supplyInput]);

  useEffect(() => {
    const t = (folioInput || '').trim();
    const h = setTimeout(() => setFolioText(t), 350);
    return () => clearTimeout(h);
  }, [folioInput]);

  useEffect(() => {
    const ids = inventory.map((r) => r.id).filter(Boolean);
    const missing = ids.filter((id) => folioMap[id] === undefined);

    if (!missing.length) return;

    Promise.all(
      missing.map((id) =>
        getItemFolioRanges(id)
          .then((ranges) => [id, Array.isArray(ranges) ? ranges : []])
          .catch(() => [id, []])
      )
    ).then((entries) => {
      setFolioMap((prev) => {
        const next = { ...prev };
        for (const [id, ranges] of entries) {
          next[id] = ranges;
        }
        return next;
      });
    });
  }, [inventory, folioMap]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const [st, cat] = await Promise.all([getStatuses(), getCategories()]);
        if (cancelled) return;
        setStatuses(st || []);
        setCategories(cat || []);
      } catch (e) {
        console.error('Error cargando catálogos', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!statuses.length) return;

    let cancelled = false;

    const fetchReportsData = async () => {
      setLoading(true);
      try {
        const ventasStatusId = statuses.find((s) => s.name === 'Ventas')?.id ?? null;
        const isVentasStatus = Number(selectedStatus) === Number(ventasStatusId);
        const isVentaConcept = movementType === 'SALE';

        const useSoldFolios = isVentasStatus || isVentaConcept;

        if (useSoldFolios) {
          const [invResp, ranges, detailed] = await Promise.all([
            getInventoryReports(Number(selectedCategory) || 0, Number(selectedStatus) || 0, {
              page,
              pageSize,
              folioText,
              movementType,
              supplyText
            }),
            getSoldFolioRanges({ saleId: 0, categoryId: Number(selectedCategory) || 0, folioText }),
            getSoldFoliosDetailed({ saleId: 0, categoryId: Number(selectedCategory) || 0, folioText }),
          ]);

          if (cancelled) return;

          setInventory(Array.isArray(invResp?.rows) ? invResp.rows : []);
          setTotalCount(Number(invResp?.count) || 0);
          setFolioRanges(Array.isArray(ranges) ? ranges : []);
          setFolioDetailed(Array.isArray(detailed) ? detailed : []);

          console.log('[REPORTS] inventory rows:', Array.isArray(invResp?.rows) ? invResp.rows : []);
          console.log('[REPORTS] folioRanges:', Array.isArray(ranges) ? ranges : []);
          console.log('[REPORTS] folioDetailed:', Array.isArray(detailed) ? detailed : []);

          console.log('[REPORTS] resumen', {
            totalInventory: Array.isArray(invResp?.rows) ? invResp.rows.length : 0,
            totalRanges: Array.isArray(ranges) ? ranges.length : 0,
            totalDetailed: Array.isArray(detailed) ? detailed.length : 0,
          });
        } else {
          const invResp = await getInventoryReports(
            Number(selectedCategory) || 0,
            Number(selectedStatus) || 0,
            {
              page,
              pageSize,
              folioText,
              movementType,
              supplyText,
            }
          );

          if (cancelled) return;

          const inventoryRows = Array.isArray(invResp?.rows) ? invResp.rows : [];
          const visibleItemIds = inventoryRows.map((r) => Number(r.id)).filter(Boolean);

          const ranges = await getInventoryReportFolios(
            Number(selectedCategory) || 0,
            Number(selectedStatus) || 0,
            folioText,
            movementType,
            visibleItemIds
          );

          if (cancelled) return;

          setInventory(inventoryRows);
          setTotalCount(Number(invResp?.count) || 0);
          setFolioRanges(Array.isArray(ranges) ? ranges : []);
          setFolioDetailed([]);

          console.log('[REPORTS] visibleItemIds:', visibleItemIds);
          console.log('[REPORTS] inventory rows:', inventoryRows);
          console.log('[REPORTS] folioRanges:', Array.isArray(ranges) ? ranges : []);
        }

        const tt = await getInventoryReportTotals(
          Number(selectedCategory) || 0,
          Number(selectedStatus) || 0,
          folioText,
          movementType,
          supplyText,
        );

        if (!cancelled) setTotals(Array.isArray(tt) ? tt : []);
      } catch (e) {
        console.error('Error cargando reportes de inventario', e);
        if (!cancelled) {
          setInventory([]);
          setTotalCount(0);
          setTotals([]);
          setFolioRanges([]);
          setFolioDetailed([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchReportsData();

    return () => {
      cancelled = true;
    };
  }, [statuses, selectedCategory, selectedStatus, folioText, supplyText, movementType, page, pageSize]);

  useEffect(() => {
    setFolioMap({});
  }, [selectedCategory, selectedStatus, folioText, supplyText, movementType, page, pageSize]);

  const loadFoliosForItems = async (items = []) => {
    const ids = items.map((r) => r.id).filter(Boolean);

    const entries = await Promise.all(
      ids.map((id) =>
        getItemFolioRanges(id)
          .then((ranges) => [id, Array.isArray(ranges) ? ranges : []])
          .catch(() => [id, []])
      )
    );

    const map = {};
    for (const [id, ranges] of entries) {
      map[id] = ranges;
    }

    return map;
  };

  const buildExportRows = (items = [], map = {}) =>
    items.map((item) => ({
      nombre: item.name || '—',
      clave: item.supplyCodes || '—',
      descripcion: item.description || '—',
      categoria: item.categoryName || '—',
      cantidad: `${Number(item.quantity || 0).toLocaleString('es-MX')} ${(item.unitOfWeightMeasurement || '').toLowerCase()}`.trim(),
      folios: (map[item.id] && map[item.id].length)
        ? formatFoliosForCell(map[item.id])
        : 'Sin folios',
      ubicacion: item.locationName || '—',
      estatus: item.statusName || '—',
      concepto: (() => {
        const t = String(item.movementType || '').trim().toUpperCase();
        const labels = {
          SALE: 'Venta',
          COURTESY: 'Cortesía',
          WASTE: 'Merma',
          PROMOTION: 'Promoción',
          TASTING: 'Degustación',
        };
        return labels[t] || '—';
      })(),
      fechaEntrada: item.acquisitionDateHumanFormatted || displayDate(item.acquisitionDate),
      fechaSalida: displayOutDate(item.outDate),
      costoUnitario: item.unitPrice ?? 0,
      asignadoA: item.assignedToUser || 'Sistema',
    }));

  const downloadExcel = async () => {
    if (loading || isExporting) return;

    setIsExporting(true);
    try {
      const fullInv = await getInventoryReportFull(selectedCategory, selectedStatus, folioText, movementType, supplyText);

      const safeFullInv = Array.isArray(fullInv) ? fullInv : [];
      const fullFolioMap = await loadFoliosForItems(safeFullInv);
      const exportRows = buildExportRows(safeFullInv, fullFolioMap);

      const ranges = safeFullInv.flatMap((item) =>
        (fullFolioMap[item.id] || []).map((r) => ({
          itemId: item.id,
          itemName: item.name,
          units: r.units,
          startFolioName: r.startFolioName,
          endFolioName: r.endFolioName,
          count: r.count,
        }))
      );

      const detailed = safeFullInv.flatMap((item) =>
        (fullFolioMap[item.id] || []).flatMap((r) =>
          Array.isArray(r.folios)
            ? r.folios.map((f) => ({
              itemId: item.id,
              itemName: item.name,
              folioId: f.id,
              folioName: f.name,
            }))
            : []
        )
      );

      const exportTotals = await getInventoryReportTotals(
        Number(selectedCategory) || 0,
        Number(selectedStatus) || 0,
        folioText,
        movementType,
        supplyText
      );

      const worker = new Worker('/excelWorker.js');

      worker.onmessage = (e) => {
        const { ok, wbout, error } = e.data || {};
        if (!ok) {
          console.error('Error en worker Excel:', error);
          alert('Ocurrió un error al generar el Excel.');
          setIsExporting(false);
          worker.terminate();
          return;
        }
        try {
          const today = new Date().toISOString().slice(0, 10);
          const blob = new Blob([wbout], { type: 'application/octet-stream' });
          saveAs(blob, `Reporte-Inventario-${today}.xlsx`);
        } finally {
          setIsExporting(false);
          worker.terminate();
        }
      };

      worker.onerror = (err) => {
        console.error('Worker error:', err);
        alert('Ocurrió un error al generar el Excel.');
        setIsExporting(false);
        worker.terminate();
      };

      worker.postMessage({
        rows: exportRows,
        totals: exportTotals
      });
    } catch (e) {
      console.error(e);
      alert('Ocurrió un error al generar el Excel.');
      setIsExporting(false);
    }
  };

  const onChangeCategory = (e) => {
    setSelectedCategory(Number(e.target.value));
    setPage(1);
  };

  const onChangeStatus = (e) => {
    setSelectedStatus(Number(e.target.value));
    setPage(1);
  };

  const onChangeFolio = (e) => {
    setFolioInput(e.target.value);
    setPage(1);
  };

  const onChangeSupply = (e) => {
    setSupplyInput(e.target.value);
    setPage(1);
  };

  const onChangeMovementType = (e) => {
    setMovementType(e.target.value);
    setPage(1);
  };

  const onChangePageSize = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  const loadFullPdfData = async () => {
    setLoadingPdfData(true);
    try {
      const fullInv = await getInventoryReportFull(
        selectedCategory,
        selectedStatus,
        folioText,
        movementType,
        supplyText
      );

      const safeData = Array.isArray(fullInv) ? fullInv : [];
      const fullFolioMap = await loadFoliosForItems(safeData);

      setPdfData(safeData);
      setPdfFolioMap(fullFolioMap);
    } finally {
      setLoadingPdfData(false);
    }
  };

  const currentPageLabel = (Number(totalCount) || 0) === 0 ? 0 : page;
  const safePageCount = pageCount;

  const getVisiblePages = (currentPage, totalPages, maxButtons = 5) => {
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + maxButtons - 1);

    if (end - start < maxButtons - 1) {
      start = Math.max(1, end - maxButtons + 1);
    }

    const pages = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const visiblePages = getVisiblePages(page, pageCount);

  return (
    <>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-3">
        <h2 className="mb-0">Reportes</h2>

        <div className="d-flex gap-2">
          <Button variant="primary">Agregar</Button>

          <Button
            variant="outline-primary"
            onClick={async () => {
              await loadFullPdfData();
              setPreviewOpen(true);
            }}
            disabled={loading || !inventory.length}
          >
            Visualizar PDF
          </Button>

          <Button
            variant="outline-success"
            onClick={downloadExcel}
            disabled={loading || !inventory.length || isExporting}
          >
            {isExporting ? 'Generando Excel…' : 'Descargar Excel'}
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Row className="g-3 align-items-end mb-3">
        <Col md={3}>
          <Form.Label className="fw-bold">Categorías</Form.Label>
          <Form.Select value={selectedCategory} onChange={onChangeCategory}>
            <option value={0}>Todas las categorías</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </Form.Select>
        </Col>

        <Col md={3}>
          <Form.Label className="fw-bold">Estados</Form.Label>
          <Form.Select value={selectedStatus} onChange={onChangeStatus}>
            <option value={0}>Todos los estados</option>
            {statuses.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </Form.Select>
        </Col>

        <Col md={3}>
          <Form.Label className="fw-bold">Concepto</Form.Label>
          <Form.Select value={movementType} onChange={onChangeMovementType}>
            <option value="">Todos</option>
            <option value="SALE">Venta</option>
            <option value="COURTESY">Cortesía</option>
            <option value="WASTE">Merma</option>
            <option value="PROMOTION">Promoción</option>
            <option value="TASTING">Degustación</option>
          </Form.Select>
        </Col>

        <Col md={3}>
          <Form.Label className="fw-bold">Folio</Form.Label>
          <Form.Control value={folioInput} onChange={onChangeFolio} placeholder="Ej. Nn-3749302550" />
        </Col>

        <Col md={3}>
          <Form.Label className="fw-bold">Clave</Form.Label>
          <Form.Control value={supplyInput} onChange={onChangeSupply} placeholder="Ej. EMP-001" />
        </Col>

        <Col md={3}>
          <Form.Label className="fw-bold">Por página</Form.Label>
          <Form.Select value={pageSize} onChange={onChangePageSize}>
            {[10, 20, 30, 50].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {/* Totales + Descargas */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="fw-bold">Totales</div>
      </div>

      {totalsList.length > 0 && (
        <Row className="g-2 mb-3">
          {totalsList.map((t, idx) => (
            <Col key={`${t.name}-${idx}`} xs={12} md={4} lg={3}>
              <div className="border rounded p-2 bg-white d-flex justify-content-between align-items-center">
                <div style={{ fontSize: 13, textTransform: 'capitalize' }}>{t.name}</div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{formatTotal(t)}</div>
              </div>
            </Col>
          ))}
        </Row>
      )}

      {/* Tabla */}
      <div className="table-rounded-wrapper">
        <div className="table-responsive">
          <Table bordered hover className="mb-0 align-middle">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Clave</th>
                <th>Descripción</th>
                <th>Categoría</th>
                <th>Cantidad</th>
                <th>Folios</th>
                <th>Ubicación</th>
                <th>Estatus</th>
                <th>Concepto</th>
                <th>Fecha Entrada</th>
                <th>Fecha Salida</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={11} className="text-center py-5">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Cargando...
                  </td>
                </tr>
              ) : inventory.length === 0 ? (
                <tr>
                  <td colSpan={11} className="text-center py-5">Sin registros</td>
                </tr>
              ) : (
                inventory.map((item, index) => (
                  <tr key={item.id ?? `${item.name}-${index}`}>
                    <td className="col-left">{item.name}</td>
                    <td className="col-left">{item.supplyCodes || '—'}</td>
                    <td className="col-left">{item.description}</td>
                    <td>{categoryBadge(item.categoryName)}</td>
                    <td>{`${Number(item.quantity).toLocaleString('es-MX')} ${(item.unitOfWeightMeasurement || '').toLowerCase()}`}</td>
                    <td style={{ maxWidth: 260, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      {folioMap[item.id] === undefined
                        ? 'Cargando…'
                        : getRowFolios(item).length
                          ? formatFoliosForCell(getRowFolios(item))
                          : 'Sin folios'}
                    </td>
                    <td>{item.locationName}</td>
                    <td>{statusBadge(item.statusName)}</td>
                    <td>
                      {conceptBadge(
                        item.movementType,
                        movementType || (item.isSale ? 'SALE' : '')
                      )}
                    </td>
                    <td>{item.acquisitionDateHumanFormatted || displayDate(item.acquisitionDate)}</td>
                    <td>{displayOutDate(item.outDate)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* VISUALIZACION */}
      <Offcanvas show={previewOpen} onHide={() => setPreviewOpen(false)} placement="end" style={{ width: 900 }}>
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Vista previa del reporte</Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body>
          <div className="d-flex gap-2 mb-3">
            {/* PDF real */}
            <PDFDownloadLink
              document={
                <MyReport
                  data={pdfData.length ? pdfData : inventory}
                  folioMap={Object.keys(pdfFolioMap).length ? pdfFolioMap : folioMap}
                />
              }
              fileName={`Reporte-Inventario-${new Date().toISOString().slice(0, 10)}.pdf`}
            >
              {({ loading: pdfLoading }) => (
                <Button variant="outline-primary" disabled={pdfLoading || loadingPdfData}>
                  {pdfLoading || loadingPdfData ? 'Generando PDF…' : 'Descargar PDF'}
                </Button>
              )}
            </PDFDownloadLink>

            <Button
              variant="outline-success"
              onClick={downloadExcel}
              disabled={loading || !inventory.length || isExporting}
            >
              {isExporting ? 'Generando Excel…' : 'Descargar Excel'}
            </Button>
          </div>

          <Tabs activeKey={previewTab} onSelect={(k) => setPreviewTab(k)} className="mb-3">
            <Tab eventKey="pdf" title="PDF">
              <div style={{ height: '70vh', border: '1px solid #eee' }}>
                <PDFViewer style={{ width: '100%', height: '100%' }}>
                  <MyReport
                    data={pdfData.length ? pdfData : inventory}
                    folioMap={Object.keys(pdfFolioMap).length ? pdfFolioMap : folioMap}
                  />
                </PDFViewer>
              </div>
            </Tab>

            <Tab eventKey="excel" title="Excel">
              <div className="table-responsive">
                <Table bordered hover size="sm" className="align-middle">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Clave</th>
                      <th>Descripción</th>
                      <th>Categoría</th>
                      <th>Cantidad</th>
                      <th>Folios</th>
                      <th>Ubicación</th>
                      <th>Estado</th>
                      <th>Concepto</th>
                      <th>Fecha Entrada</th>
                      <th>Fecha Salida</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inventory.map((item) => (
                      <tr key={item.id}>
                        <td>{item.name}</td>
                        <td>{item.supplyCodes || '—'}</td>
                        <td>{item.description}</td>
                        <td>{item.categoryName}</td>
                        <td>{`${Number(item.quantity).toLocaleString('es-MX')} ${(item.unitOfWeightMeasurement || '').toLowerCase()}`}</td>
                        <td style={{ maxWidth: 260, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          {folioMap[item.id] === undefined
                            ? 'Cargando…'
                            : getRowFolios(item).length
                              ? formatFoliosForCell(getRowFolios(item))
                              : 'Sin folios'}
                        </td>
                        <td>{item.locationName}</td>
                        <td>{statusBadge(item.statusName)}</td>
                        <td>
                          {conceptBadge(
                            item.movementType,
                            movementType || (item.isSale ? 'SALE' : '')
                          )}
                        </td>
                        <td>{item.acquisitionDateHumanFormatted}</td>
                        <td>{displayOutDate(item.outDate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Tab>
          </Tabs>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Paginación*/}
      <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
        <div className="text-muted">
          Página {currentPageLabel} de {safePageCount} ({Number(totalCount) || 0} registros)
        </div>

        <div className="d-flex gap-2 align-items-center flex-wrap">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setPage(1)}
            disabled={page <= 1 || safePageCount === 0}
          >
            «
          </Button>

          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || safePageCount === 0}
          >
            Anterior
          </Button>

          {visiblePages[0] > 1 && (
            <>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(1)}>
                1
              </Button>
              {visiblePages[0] > 2 && <span className="px-1 text-muted">…</span>}
            </>
          )}

          {visiblePages.map((pageNumber) => (
            <Button
              key={pageNumber}
              size="sm"
              variant={pageNumber === page ? 'secondary' : 'outline-secondary'}
              onClick={() => setPage(pageNumber)}
            >
              {pageNumber}
            </Button>
          ))}

          {visiblePages[visiblePages.length - 1] < safePageCount && (
            <>
              {visiblePages[visiblePages.length - 1] < safePageCount - 1 && (
                <span className="px-1 text-muted">…</span>
              )}
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setPage(safePageCount)}
              >
                {safePageCount}
              </Button>
            </>
          )}

          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setPage((p) => Math.min(safePageCount, p + 1))}
            disabled={safePageCount === 0 || page >= safePageCount}
          >
            Siguiente
          </Button>

          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setPage(safePageCount)}
            disabled={safePageCount === 0 || page >= safePageCount}
          >
            »
          </Button>
        </div>
      </div>
    </>
  );
};

export default Reports;